import type { StatsPeriod, StatsPoint } from "@shared/types";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Clock3,
  ExternalLink,
  Gauge,
  Loader2,
  Pause,
  Pencil,
  Play,
  RotateCcw,
  ServerOff,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CopyButton } from "@/components/copy-button";
import { EmptyState } from "@/components/empty-state";
import { MonitorEventTable } from "@/components/monitors/event-table";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { UptimeBars } from "@/components/uptime-bars";
import {
  ApiError,
  deleteMonitor,
  getHeartbeats,
  getMonitor,
  getMonitorEvents,
  getMonitorStats,
  pauseMonitor,
  resetPushToken,
  resumeMonitor,
} from "@/lib/api";
import { queryKeys, useMonitors } from "@/lib/queries";
import { cn, formatLatency, formatUptime } from "@/lib/utils";

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function DetailSkeleton() {
  return (
    <div className="p-6 md:p-8 space-y-6 max-w-6xl mx-auto w-full">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <Skeleton className="h-7 w-56" />
          <Skeleton className="h-4 w-80" />
        </div>
        <Skeleton className="h-9 w-64" />
      </div>
      <Skeleton className="h-20 rounded-xl" />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-28 rounded-xl" />
        ))}
      </div>
      <Skeleton className="h-80 rounded-xl" />
    </div>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon: typeof Gauge;
}) {
  return (
    <Card className="gap-3 py-5">
      <CardHeader className="flex grid-cols-none flex-row items-center justify-between px-5">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
        <Icon className="size-4 text-muted-foreground" />
      </CardHeader>
      <CardContent className="px-5">
        <p className="text-xl font-semibold tabular-nums">{value}</p>
      </CardContent>
    </Card>
  );
}

function LatencyTooltip({
  active,
  label,
  points,
}: {
  active?: boolean;
  label?: string | number;
  points: StatsPoint[];
}) {
  if (!active || label == null) return null;
  const time = Number(label);
  const point = points.find(item => item.t === time);
  if (!point) return null;
  return (
    <div className="rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow">
      <p className="mb-1 font-medium">{new Date(point.t).toLocaleString()}</p>
      <p className="tabular-nums text-muted-foreground">
        Average: <span className="text-foreground">{formatLatency(point.avgLatency)}</span>
      </p>
      <p className="tabular-nums text-muted-foreground">
        Up: <span className="text-status-up">{point.up}</span> · Down:{" "}
        <span className="text-status-down">{point.down}</span>
      </p>
    </div>
  );
}

export default function MonitorDetailPage() {
  const params = useParams();
  const id = Number(params.id);
  const validId = Number.isInteger(id) && id > 0;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState<StatsPeriod>("24h");

  const monitorQuery = useQuery({
    queryKey: queryKeys.monitor(id),
    queryFn: () => getMonitor(id),
    enabled: validId,
  });
  const monitorsQuery = useMonitors();
  const heartbeatQuery = useQuery({
    queryKey: queryKeys.heartbeats(id, 50),
    queryFn: () => getHeartbeats(id, 50),
    enabled: validId,
    refetchInterval: 5_000,
  });
  const statsQuery = useQuery({
    queryKey: queryKeys.monitorStats(id, period),
    queryFn: () => getMonitorStats(id, period),
    enabled: validId,
    refetchInterval: 30_000,
    placeholderData: keepPreviousData,
  });
  const stats24Query = useQuery({
    queryKey: queryKeys.monitorStats(id, "24h"),
    queryFn: () => getMonitorStats(id, "24h"),
    enabled: validId,
    refetchInterval: 30_000,
    placeholderData: keepPreviousData,
  });
  const stats30Query = useQuery({
    queryKey: queryKeys.monitorStats(id, "30d"),
    queryFn: () => getMonitorStats(id, "30d"),
    enabled: validId,
    refetchInterval: 30_000,
    placeholderData: keepPreviousData,
  });
  const eventsQuery = useQuery({
    queryKey: queryKeys.monitorEvents(id),
    queryFn: () => getMonitorEvents(id, 50),
    enabled: validId,
    refetchInterval: 10_000,
  });

  const refreshMonitor = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.monitors }),
      queryClient.invalidateQueries({ queryKey: queryKeys.monitor(id) }),
    ]);
  };

  const activeMutation = useMutation({
    mutationFn: (active: boolean) => (active ? resumeMonitor(id) : pauseMonitor(id)),
    onSuccess: async (_, active) => {
      toast.success(active ? "Monitoring resumed" : "Monitoring paused");
      await refreshMonitor();
    },
    onError: error => toast.error(errorMessage(error, "Could not update monitor")),
  });
  const deleteMutation = useMutation({
    mutationFn: () => deleteMonitor(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.monitors });
      toast.success("Monitor deleted");
      navigate("/dashboard");
    },
    onError: error => toast.error(errorMessage(error, "Could not delete monitor")),
  });
  const resetTokenMutation = useMutation({
    mutationFn: () => resetPushToken(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.monitor(id) });
      toast.success("Push token reset");
    },
    onError: error => toast.error(errorMessage(error, "Could not reset push token")),
  });

  if (monitorQuery.isLoading) return <DetailSkeleton />;

  if (!validId || monitorQuery.isError || !monitorQuery.data) {
    return (
      <div className="p-6 md:p-8 space-y-6 max-w-6xl mx-auto w-full">
        <EmptyState
          icon={ServerOff}
          title="Monitor not found"
          description="This monitor may have been deleted or the link is invalid."
          action={
            <Button asChild variant="outline">
              <Link to="/dashboard">
                <ArrowLeft className="size-4" />
                Back to dashboard
              </Link>
            </Button>
          }
        />
      </div>
    );
  }

  const monitor = monitorQuery.data;
  const liveMonitor = monitorsQuery.data?.find(item => item.id === id);
  const active = liveMonitor?.active ?? monitor.active;
  const currentStatus = liveMonitor?.currentStatus ?? null;
  const lastLatency = liveMonitor?.lastLatency ?? null;
  const points = statsQuery.data?.points ?? [];
  const certExpiryDays =
    monitor.type === "http" || monitor.type === "keyword" ? monitor.certExpiryDays : null;
  const pushUrl =
    monitor.type === "push" && monitor.pushToken
      ? `${window.location.origin}/api/push/${monitor.pushToken}`
      : null;

  let description: ReactNode = "push monitor";
  if ((monitor.type === "http" || monitor.type === "keyword") && monitor.url) {
    description = (
      <a
        href={monitor.url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex max-w-full items-center gap-1 hover:text-foreground hover:underline"
      >
        <span className="truncate">{monitor.url}</span>
        <ExternalLink className="size-3 shrink-0" />
      </a>
    );
  } else if (monitor.hostname) {
    description = `${monitor.hostname}${monitor.port != null ? `:${monitor.port}` : ""}`;
  }

  const updatePeriod = (value: string) => {
    if (value === "24h" || value === "7d" || value === "30d") setPeriod(value);
  };

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-6xl mx-auto w-full">
      <PageHeader
        title={
          <span className="flex min-w-0 items-center gap-3">
            <span className="truncate">{monitor.name}</span>
            <StatusBadge status={currentStatus} paused={!active} className="shrink-0 text-sm" />
          </span>
        }
        description={description}
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => activeMutation.mutate(!active)}
              disabled={activeMutation.isPending}
            >
              {activeMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : active ? (
                <Pause className="size-4" />
              ) : (
                <Play className="size-4" />
              )}
              {active ? "Pause" : "Resume"}
            </Button>
            <Button asChild variant="outline">
              <Link to={`/monitors/${id}/edit`}>
                <Pencil className="size-4" />
                Edit
              </Link>
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button type="button" variant="destructive">
                  <Trash2 className="size-4" />
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete “{monitor.name}”?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This permanently removes the monitor and all of its heartbeat history.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    variant="destructive"
                    onClick={() => deleteMutation.mutate()}
                    disabled={deleteMutation.isPending}
                  >
                    Delete monitor
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        }
      />

      {!active && (
        <Alert className="border-status-paused/30 bg-status-paused/5">
          <Pause />
          <AlertTitle>Monitoring is paused</AlertTitle>
          <AlertDescription>No checks will run until this monitor is resumed.</AlertDescription>
        </Alert>
      )}

      <Card className="gap-3 py-5">
        <CardHeader className="flex grid-cols-none flex-row items-center justify-between px-5">
          <CardTitle className="text-sm">Recent checks</CardTitle>
          <span className="text-xs text-muted-foreground">last 50 checks · newest right</span>
        </CardHeader>
        <CardContent className="px-5">
          {heartbeatQuery.isLoading ? (
            <Skeleton className="h-8 w-full" />
          ) : (
            <UptimeBars beats={heartbeatQuery.data ?? []} slots={50} className="h-8" />
          )}
        </CardContent>
      </Card>

      <section
        aria-label="Monitor statistics"
        className={cn("grid gap-4 sm:grid-cols-2 xl:grid-cols-4", certExpiryDays != null && "xl:grid-cols-5")}
      >
        <StatCard label="Response" value={formatLatency(lastLatency)} icon={Clock3} />
        <StatCard
          label="Avg response (24h)"
          value={formatLatency(stats24Query.data?.avgLatency)}
          icon={Gauge}
        />
        <StatCard label="Uptime (24h)" value={formatUptime(stats24Query.data?.uptime)} icon={Gauge} />
        <StatCard label="Uptime (30d)" value={formatUptime(stats30Query.data?.uptime)} icon={Gauge} />
        {certExpiryDays != null && (
          <Card className="gap-3 py-5">
            <CardHeader className="flex grid-cols-none flex-row items-center justify-between px-5">
              <CardTitle className="text-sm font-medium text-muted-foreground">Certificate</CardTitle>
              <ShieldCheck className="size-4 text-muted-foreground" />
            </CardHeader>
            <CardContent className="px-5">
              <p
                className={cn(
                  "text-xl font-semibold tabular-nums",
                  certExpiryDays < 7
                    ? "text-status-down"
                    : certExpiryDays < 14
                      ? "text-status-pending"
                      : "text-muted-foreground",
                )}
              >
                TLS expires in {certExpiryDays}d
              </p>
            </CardContent>
          </Card>
        )}
      </section>

      {pushUrl && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Push URL</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2">
              <code className="min-w-0 flex-1 truncate text-xs">{pushUrl}</code>
              <CopyButton value={pushUrl} />
            </div>
            <code className="block overflow-x-auto whitespace-nowrap text-xs text-muted-foreground">
              curl -fsS &quot;{pushUrl}?status=up&amp;ping=42&amp;msg=OK&quot;
            </code>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button type="button" variant="outline" size="sm">
                  <RotateCcw className="size-4" />
                  Reset token
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Reset this push token?</AlertDialogTitle>
                  <AlertDialogDescription>
                    The current URL will stop working immediately. Update every sender with the new URL.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    variant="destructive"
                    onClick={() => resetTokenMutation.mutate()}
                    disabled={resetTokenMutation.isPending}
                  >
                    Reset token
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex grid-cols-none flex-row items-center justify-between gap-4">
          <CardTitle className="text-base">Response time</CardTitle>
          <Tabs value={period} onValueChange={updatePeriod}>
            <TabsList>
              <TabsTrigger value="24h">24h</TabsTrigger>
              <TabsTrigger value="7d">7d</TabsTrigger>
              <TabsTrigger value="30d">30d</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent>
          {statsQuery.isLoading ? (
            <Skeleton className="h-[260px] w-full" />
          ) : points.length === 0 ? (
            <div className="flex h-[260px] items-center justify-center text-sm text-muted-foreground">
              No data yet
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="latencyFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--status-up)" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="var(--status-up)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="currentColor" strokeOpacity={0.1} vertical={false} />
                <XAxis
                  dataKey="t"
                  tickLine={false}
                  axisLine={false}
                  minTickGap={24}
                  tick={{ fontSize: 11 }}
                  tickFormatter={value => {
                    const date = new Date(Number(value));
                    return period === "24h"
                      ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                      : `${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
                  }}
                />
                <YAxis
                  width={40}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 11 }}
                  tickFormatter={value => formatLatency(Number(value))}
                />
                <Tooltip
                  cursor={{ stroke: "var(--border)" }}
                  content={props => <LatencyTooltip active={props.active} label={props.label} points={points} />}
                />
                <Area
                  type="monotone"
                  dataKey="avgLatency"
                  stroke="var(--status-up)"
                  strokeWidth={2}
                  fill="url(#latencyFill)"
                  connectNulls
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 border-t pt-4 text-xs text-muted-foreground">
            <span>Min <strong className="font-medium text-foreground tabular-nums">{formatLatency(statsQuery.data?.minLatency)}</strong></span>
            <span>Avg <strong className="font-medium text-foreground tabular-nums">{formatLatency(statsQuery.data?.avgLatency)}</strong></span>
            <span>Max <strong className="font-medium text-foreground tabular-nums">{formatLatency(statsQuery.data?.maxLatency)}</strong></span>
          </div>
        </CardContent>
      </Card>

      <Card className="gap-3">
        <CardHeader>
          <CardTitle className="text-base">Events</CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          {eventsQuery.isLoading ? (
            <div className="space-y-2 px-6 pb-4">
              {Array.from({ length: 4 }, (_, index) => (
                <Skeleton key={index} className="h-10 w-full" />
              ))}
            </div>
          ) : (
            <MonitorEventTable events={eventsQuery.data ?? []} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
